import React from "react";
import { Edit, Edit2, ImportCurve, Send } from "iconsax-react";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { PERMISSIONS } from "@/shared/consts";
import type { SigningStatus } from "@/shared/types";
import type { AmendmentDetailResponse, AmendmentStatus } from "../types";

export type AmendmentAction =
  | "approve"
  | "reject_draft"
  | "request_revision"
  | "revoke"
  | "sign_order"
  | "download_order_docx"
  | "cancel_order_signing"
  | "submit_agreement";

export interface ActionConfig {
  id: AmendmentAction;
  label: string;
  variant: "primary" | "secondary" | "danger";
  icon: React.ReactNode;
}

export interface AmendmentActionsConfig {
  actions: ActionConfig[];
  isLoading: boolean;
}

interface IsLoadingState {
  isApproving: boolean;
  isRejecting: boolean;
  isRequestingRevision: boolean;
  isSigning: boolean;
  isDownloadingOrder: boolean;
  isSubmittingAgreement: boolean;
}

export function getAvailableActions(
  amendment: AmendmentDetailResponse,
  isLoading: IsLoadingState,
  t: (key: string) => string,
  orderSigningStatus: SigningStatus | null = null,
): AmendmentActionsConfig {
  const status = amendment.status as AmendmentStatus;
  const actions: ActionConfig[] = [];

  const hasRevisionNote = amendment.application_review_note != null && amendment.application_review_note !== "";
  const canApproveDraft = status === "draft" && !hasRevisionNote;
  const canRejectDraft = status === "draft" && !hasRevisionNote;
  const canRequestRevision = status === "draft" && !hasRevisionNote;
  const canRevoke = status === "signing_pending" || status === "agr_pending";
  const canSignOrder = status === "order_pending";
  const canSubmitAgreement = status === "order_uploaded";
  const canCancelOrderSigning = canSignOrder && amendment.order_id != null && orderSigningStatus === 0;
  const canDownloadOrderDocx =
    (status === "order_pending" || status === "agr_pending" || status === "agreement_signed" || status === "applied") &&
    !amendment.order_signed_pdf_url;

  if (canApproveDraft) {
    actions.push({
      id: "approve",
      label: isLoading.isApproving || isLoading.isSigning
        ? t("amendment.actions.approving")
        : t("amendment.actions.approve"),
      variant: "primary",
      icon: React.createElement(CheckIcon),
    });
  }

  if (canRequestRevision) {
    actions.push({
      id: "request_revision",
      label: isLoading.isRequestingRevision
        ? t("amendment.actions.requesting")
        : t("amendment.actions.requestRevision"),
      variant: "secondary",
      icon: React.createElement(Edit2, { size: 16, color: "currentColor" }),
    });
  }

  if (canRejectDraft) {
    actions.push({
      id: "reject_draft",
      label: isLoading.isRejecting ? t("amendment.actions.rejecting") : t("amendment.actions.reject"),
      variant: "danger",
      icon: React.createElement(CloseIcon),
    });
  }

  if (canRevoke) {
    actions.push({
      id: "revoke",
      label: t("amendment.actions.revoke"),
      variant: "danger",
      icon: React.createElement(CloseIcon),
    });
  }

  if (canSignOrder) {
    actions.push({
      id: "sign_order",
      label: t("amendment.actions.signOrder"),
      variant: "primary",
      icon: React.createElement(Edit, { size: 16, color: "currentColor" }),
    });
  }

  if (canDownloadOrderDocx) {
    actions.push({
      id: "download_order_docx",
      label: isLoading.isDownloadingOrder
        ? t("amendment.actions.downloading")
        : t("amendment.actions.downloadOrderDocx"),
      variant: "secondary",
      icon: React.createElement(ImportCurve, { size: 16, color: "currentColor" }),
    });
  }

  if (canCancelOrderSigning) {
    actions.push({
      id: "cancel_order_signing",
      label: t("Common:signing.cancelSigning.button"),
      variant: "secondary",
      icon: React.createElement(CloseIcon),
    });
  }

  if (canSubmitAgreement) {
    actions.push({
      id: "submit_agreement",
      label: isLoading.isSubmittingAgreement
        ? t("amendment.actions.submitting")
        : t("amendment.actions.submitAgreement"),
      variant: "primary",
      icon: React.createElement(Send, { size: 16, color: "currentColor" }),
    });
  }

  const isLoadingAny =
    isLoading.isApproving ||
    isLoading.isRejecting ||
    isLoading.isRequestingRevision ||
    isLoading.isSigning ||
    isLoading.isDownloadingOrder ||
    isLoading.isSubmittingAgreement;

  return {
    actions,
    isLoading: isLoadingAny,
  };
}

export function filterActionsByPermissions(
  actions: ActionConfig[],
  hasPermission: (p: string) => boolean,
  hasRole: (r: string) => boolean,
  showDomainRevoke = false,
): ActionConfig[] {
  return actions.filter(function filterByPermission(action) {
    if (action.id === "revoke") {
      return showDomainRevoke;
    }
    if (
      action.id === "approve" ||
      action.id === "sign_order" ||
      action.id === "cancel_order_signing" ||
      action.id === "submit_agreement"
    ) {
      return hasPermission(PERMISSIONS.HR.APPROVE_AMENDMENT) && hasRole("chief");
    }
    if (action.id === "reject_draft" || action.id === "request_revision") {
      return hasPermission(PERMISSIONS.HR.APPROVE_AMENDMENT);
    }
    return true;
  });
}

interface RevocableSigningDocument {
  status: number;
  org_signer_iin: string;
}

export function isSignerRevokeVisible(
  document: RevocableSigningDocument | null | undefined,
  userIin: string | null | undefined,
): boolean {
  if (!document || (document.status !== 0 && document.status !== 1)) return false;
  return Boolean(userIin) && userIin === document.org_signer_iin;
}

export function isDomainRevokeVisible(
  amendment: Pick<AmendmentDetailResponse, "signing_document" | "agreement_signing_document" | "can_revoke">,
  userIin: string | null | undefined,
): boolean {
  const signerButtonShown =
    isSignerRevokeVisible(amendment.signing_document, userIin) ||
    isSignerRevokeVisible(amendment.agreement_signing_document, userIin);
  return amendment.can_revoke && !signerButtonShown;
}
